import { useEffect, useState, type PropsWithChildren, type ReactNode } from "react";
import type { PendingGift, WorkStep } from "@studio/shared";
import { InlineSpinner } from "./InlineSpinner";
import type { AutosaveStatus } from "../lib/autosave";
import { useExitGuard } from "../hooks/useExitGuard";
import { navigate } from "../lib/navigation";
import { clearSession, loadSession, updateSession, type StudentSession } from "../lib/session";
import { apiRequest, getMe, remainingSlotsEvent } from "../lib/api";
import {
  confirmPendingGift,
  currentPendingGift,
  subscribePendingGifts,
} from "../lib/gifts";

const musicStepLabels = ["만들기 방식", "가사", "소개·스타일", "완성"] as const;
export const mvStepLabels = ["노래 선택", "스토리보드", "컷 만들기", "편집·완성"] as const;
/** 그림 수업(2026-09-24) — 서버 단계 `pic_pick·pic_image·pic_song·pic_done` 과 1:1 */
export const pictureStepLabels = ["그림 고르기", "그림 완성", "노래 붙이기", "작품 카드"] as const;
/** 포스터 수업(2026-10-01) — 서버 단계 `pst_draw·pst_edit·pst_song·pst_done` 과 1:1 */
export const posterStepLabels = ["그림 만들기", "편집하기", "노래 · AR", "인쇄 확정"] as const;

export const mvStudioStep: Record<Extract<WorkStep,
  "song" | "story" | "cuts" | "mv_done" | "edit" | "final"
>, 1 | 2 | 3 | 4> = {
  song: 1,
  story: 2,
  cuts: 3,
  mv_done: 4,
  edit: 4,
  final: 4,
};

export interface StudioExit {
  /** 나간 뒤 도착할 곳. */
  path: string;
  /** 헤더 버튼 글자. */
  label: string;
  /** 확인 모달 제목. */
  title: string;
  /** 확인 모달 본문 — 나가면 무슨 일이 벌어지는지 사실대로. */
  message: string;
  /** 확인 모달의 실행 버튼 글자. */
  confirmLabel: string;
}

/**
 * 「나가기」의 목적지와 문구 — **누가 나가느냐**로 갈린다(2026-08-07 QA).
 *
 * 예전에는 누구든 `/join`(학생 코드 입력)으로 보냈다. 그래서 시연작(§9.H-14 B형)으로
 * 학생 화면에 들어와 있던 **교사**가 뒤로가기 한 번에 «참여 코드를 입력하세요» 화면으로
 * 떨어졌다. 같은 앱의 「시연 끝내기」(TeacherDemoBar)는 이미 `/teacher` 로 가고 있었으니,
 * 나가는 문 둘이 서로 다른 곳을 가리키고 있었다.
 *
 * 🔑 학생 문구도 함께 고친다. 「이 기기에 24시간 보관돼요, 나중에 이어서 만들 수 있어요」는
 *    **사실이 아니었다** — 나가면 로컬 세션이 지워지고, 같은 이름으로 다시 들어가도 서버는
 *    새 참여자·새 작업을 만든다(`SessionRepository.join`). 돌아올 길이 없다.
 */
export function studioExitTarget(session: StudentSession | null): StudioExit {
  if (session?.demo === true) {
    return {
      // 🔑 «온 곳» = 시연을 켠 그 수업의 현황이다(B1, 2026-08-12).
      //    종전에는 수업 목록(/teacher)으로 보냈다 — 시연이 «독립 수업» 이던 시절엔 그게 온 곳이었지만,
      //    이제 시연은 «이 수업 안의 작품» 이라 온 곳은 그 수업이다.
      //    demoSessionId 가 없으면(옛 로컬 세션) 목록으로 떨어뜨린다 — 없는 수업으로 보내지 않는다.
      path: session.demoSessionId
        ? `/teacher/live/${encodeURIComponent(session.demoSessionId)}`
        : "/teacher",
      label: "← 시연 끝내기",
      title: "시연을 끝낼까요?",
      // 🚨 「시연 세션은 그대로 남아요」는 더 이상 사실이 아니다 — 시연은 세션이 아니라 작품이다.
      message: "만든 시연작은 이 수업에 그대로 남아요. 다시 누르면 이어서 만들 수 있어요.",
      confirmLabel: "시연 끝내기",
    };
  }
  return {
    path: "/join",
    label: "← 수업에서 나가기",
    title: "수업에서 나갈까요?",
    // 🚨 이 문구는 서버의 재참여 규칙과 짝이다. 서버가 «같은 이름이면 하던 작업으로 복귀» 로
    //    바뀌었으므로(2026-08-08), 예전의 「다시 돌아올 수 없어요」 는 더 이상 사실이 아니다.
    message: "같은 이름으로 다시 들어오면 하던 작업을 이어서 만들 수 있어요. 완성한 노래는 선생님 화면에 그대로 남아요.",
    confirmLabel: "수업에서 나가기",
  };
}

export interface StudioShellProps extends PropsWithChildren {
  currentStep: 1 | 2 | 3 | 4;
  remainingSlots?: number | undefined;
  imageRegenRemaining?: number | undefined;
  dirty?: boolean | undefined;
  autosaveStatus?: AutosaveStatus | undefined;
  notice?: ReactNode | undefined;
  /** PC·크롬북 전용 넓은 작업 화면(포스터 편집기). 기본 틀은 1100px 이다 */
  wide?: boolean | undefined;
  /** 단계 칸을 눌러 옮겨 다닐 수 있게 한다(포스터 — 앞 세 칸은 자유롭게 오간다) */
  onStepClick?: ((step: 1 | 2 | 3 | 4) => void) | undefined;
}

export function StudioShell({
  currentStep,
  remainingSlots,
  imageRegenRemaining,
  dirty = false,
  autosaveStatus,
  notice,
  wide = false,
  onStepClick,
  children,
}: StudioShellProps) {
  const session = loadSession();
  const [displayedRemaining, setDisplayedRemaining] = useState(remainingSlots);
  const [displayedImageRemaining, setDisplayedImageRemaining] = useState(imageRegenRemaining);
  const [gift, setGift] = useState<PendingGift | null>(currentPendingGift);
  const [confirmingGift, setConfirmingGift] = useState(false);
  const [giftError, setGiftError] = useState<string | null>(null);
  const [endedWithActiveJob, setEndedWithActiveJob] = useState(false);
  const exit = studioExitTarget(session);
  const exitGuard = useExitGuard({
    when: true,
    dirty,
    message: exit.message,
    onExit: () => {
      clearSession("exit", undefined, session?.workId);
      navigate(exit.path, { replace: true });
    },
  });
  const saveLabel = autosaveStatus === "saving"
    ? "저장 중"
    : autosaveStatus === "saved"
      ? "저장됨"
      : autosaveStatus === "error"
        ? "저장 연결 끊김"
        : null;

  useEffect(() => setDisplayedRemaining(remainingSlots), [remainingSlots]);
  useEffect(() => setDisplayedImageRemaining(imageRegenRemaining), [imageRegenRemaining]);

  useEffect(() => subscribePendingGifts(() => setGift(currentPendingGift())), []);

  useEffect(() => {
    let cancelled = false;
    let timer: number | null = null;
    const poll = async (): Promise<void> => {
      try {
        const me = await getMe();
        if (cancelled) return;
        setDisplayedRemaining(me.remaining_slots);
        setDisplayedImageRemaining(me.image_regen_remaining);
        window.dispatchEvent(new CustomEvent(remainingSlotsEvent, { detail: me.remaining_slots }));
        if (me.active_job) updateSession({ activeJobId: me.active_job.id }, me.work_id);
        window.dispatchEvent(new CustomEvent("mvs:me-state", { detail: me }));
        // 결과 화면에 있으면 저장된 단계가 아직 result 가 아니어도(예약 뒤 단계 저장 실패) 곡 고르기를 지킨다.
        const awaitingResultChoice = me.step === "result" || window.location.pathname.endsWith("/result");
        setEndedWithActiveJob(
          me.session_status === "ended" && (me.active_job !== null || awaitingResultChoice),
        );
        if (me.session_status === "ended" && me.active_job === null && !awaitingResultChoice) {
          window.dispatchEvent(new Event("mvs:session-ended"));
          return;
        }
      } catch {
        // Low-frequency delivery polling is best effort; page-specific requests retain their state.
      }
      if (!cancelled) timer = window.setTimeout(() => void poll(), 15_000);
    };
    void poll();
    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, []);

  const acknowledgeGift = async (): Promise<void> => {
    if (!gift || confirmingGift) return;
    setConfirmingGift(true);
    setGiftError(null);
    try {
      await confirmPendingGift(async (upToSeq) => {
        const result = await apiRequest<{ ok: true; remaining_slots: number }>("/api/gifts/ack", {
          method: "POST",
          body: JSON.stringify({ up_to_seq: upToSeq }),
        });
        setDisplayedRemaining(result.remaining_slots);
        window.dispatchEvent(new CustomEvent(remainingSlotsEvent, { detail: result.remaining_slots }));
        const me = await getMe();
        setDisplayedImageRemaining(me.image_regen_remaining);
      });
      setGift(currentPendingGift());
    } catch {
      setGiftError("확인을 보내지 못했어요. 다시 눌러 주세요.");
    } finally {
      setConfirmingGift(false);
    }
  };

  const giftMessage = (pending: PendingGift): string => {
    switch (pending.kind) {
      case "music_slot":
        return `선생님이 기회를 ${pending.amount}곡 더 주셨어요`;
      case "image_regen":
        return `선생님이 그림 다시 만들기 ${pending.amount}회를 더 주셨어요`;
      case "video_grant":
        return `선생님이 AI 영상 기회를 ${pending.amount}회 더 주셨어요`;
    }
  };
  const stepLabels = session?.kind === "mv"
    ? mvStepLabels
    : session?.kind === "image"
      ? pictureStepLabels
      : session?.kind === "poster"
        ? posterStepLabels
        : musicStepLabels;

  return (
    <main className="studio-page-shell">
      <section className={`studio-frame${wide ? " studio-frame-wide" : ""}`}>
        <header className="studio-header">
          <button type="button" className="button-ghost studio-exit" onClick={exitGuard.requestExit}>
            {exit.label}
          </button>
          <ol className="studio-stepbar" aria-label="스튜디오 진행 단계">
            {stepLabels.map((label, index) => {
              const number = index + 1;
              const state = number < currentStep ? "done" : number === currentStep ? "current" : "next";
              return (
                <li className={`studio-step studio-step-${state}`} key={label}>
                  {onStepClick ? (
                    <button
                      type="button"
                      className="studio-step-button"
                      aria-current={state === "current" ? "step" : undefined}
                      onClick={() => onStepClick(number as 1 | 2 | 3 | 4)}
                    >
                      <span aria-hidden="true">{state === "done" ? "✓" : number}</span>
                      <b>{label}</b>
                    </button>
                  ) : (
                    <>
                      <span aria-hidden="true">{state === "done" ? "✓" : number}</span>
                      <b>{label}</b>
                    </>
                  )}
                </li>
              );
            })}
          </ol>
          <div className="studio-student-meta">
            {session?.name ? <span>{session.name}</span> : null}
            {/* 🔑 그림 수업에는 «곡 생성» 이 없다 — 폴링이 채우는 곡 칸을 띄우면 없는 한도를 보여 준다. */}
            {displayedRemaining !== undefined && session?.kind !== "image" && session?.kind !== "poster"
              ? <span>남은 생성 {displayedRemaining}곡</span>
              : null}
            {(session?.kind === "mv" || session?.kind === "image" || session?.kind === "poster") && displayedImageRemaining !== undefined
              ? <span>그림 다시 {session?.kind === "poster" ? "그리기" : "만들기"} {displayedImageRemaining}회</span>
              : null}
            {saveLabel ? <span className={autosaveStatus === "error" ? "text-danger" : ""}>{saveLabel}</span> : null}
          </div>
        </header>
        {endedWithActiveJob ? (
          <div className="studio-notice">
            수업이 끝났어요. 만들던 작업의 마지막 상태를 확인하고 있어요
          </div>
        ) : null}
        {notice ? <div className="studio-notice">{notice}</div> : null}
        {children}
        {exitGuard.isOpen ? (
          <div
            className="exit-dialog-backdrop"
            onClick={(event) => {
              if (event.target === event.currentTarget) exitGuard.cancelExit();
            }}
          >
            <div
              ref={exitGuard.dialogRef}
              className="exit-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="exit-dialog-title"
              aria-describedby="exit-dialog-description"
              tabIndex={-1}
            >
              <button className="button-ghost exit-dialog-close" type="button" onClick={exitGuard.cancelExit}>
                <span aria-hidden="true">×</span>
                닫기
              </button>
              <h2 id="exit-dialog-title">{exit.title}</h2>
              <p id="exit-dialog-description">{exitGuard.message}</p>
              <div className="exit-dialog-actions">
                <button
                  className="button-secondary"
                  type="button"
                  data-exit-dialog-cancel
                  onClick={exitGuard.cancelExit}
                >
                  취소
                </button>
                <button className="button-primary" type="button" onClick={exitGuard.confirmExit}>
                  {exit.confirmLabel}
                </button>
              </div>
            </div>
          </div>
        ) : null}
        {gift ? (
          <div className="gift-toast" role="status" aria-live="polite">
            <span aria-hidden="true">🎁</span>
            <div>
              <strong>{giftMessage(gift)}</strong>
              {giftError ? <small className="text-danger">{giftError}</small> : null}
            </div>
            <button
              className="button-primary"
              type="button"
              disabled={confirmingGift}
              onClick={() => void acknowledgeGift()}
            >
              {confirmingGift ? <InlineSpinner label="확인 중..." /> : "확인"}
            </button>
          </div>
        ) : null}
      </section>
    </main>
  );
}

export function LoadingPage({ label = "불러오는 중..." }: { label?: string }) {
  return (
    <main className="center-page" aria-busy="true">
      <div className="loading-dot" aria-hidden="true" />
      <p>{label}</p>
    </main>
  );
}

/**
 * 화면을 못 여는 «끝난 실패» 를 보여준다.
 *
 * 🚨 이 자리에 LoadingPage 를 쓰면 안 된다. 11개 화면이 그렇게 하고 있었고(2026-08-04 순찰),
 * 결과가 이랬다:
 *   · 점이 **영원히 돈다** — 재시도도 돌아갈 길도 없다.
 *   · `aria-busy="true"` 라 스크린리더가 «아직 불러오는 중» 이라고 안내한다. 실제로는 이미 끝난 실패다.
 *   · 순찰 크롤러가 «흰화면(지속 스피너)» 으로 판정한다 — 자동 검사에서도 죽은 화면으로 보인다.
 *
 * 서버는 이유를 정확히 내려준다(예: 422 "완성한 노래에서만 코드를 볼 수 있어요").
 * 그 문장을 그대로 보여주고, **나가는 문**을 준다.
 */
/**
 * ErrorPage 의 «나가는 문» 을 정한다.
 *
 * 🚨 **조건부가 아니다 — 항상 갈 곳을 돌려준다.** 세션이 없을 때 이 버튼을 감췄더니
 * 남는 건 「다시 시도」 하나뿐이었고, 그건 눌러도 같은 실패로 돌아온다. 막다른 길이다.
 * (2026-08-05 prod 육안 확인에서 실제로 그랬다 — 링크만 받고 들어온 학생은
 *  «인증이 필요합니다 / 다시 시도» 에 갇혔다.)
 *
 * 세션이 있으면 하던 작품으로, 없으면 참여 화면으로.
 */
export function errorPageExit(session: StudentSession | null): { path: string; label: string } {
  if (!session) return { path: "/join", label: "수업 참여하기" };
  const mode = session.kind === "mv"
    ? "mv"
    : session.kind === "image" ? "pic" : session.kind === "poster" ? "poster" : "music";
  return {
    path: `/${mode}/w/${encodeURIComponent(session.workId)}`,
    label: "내 작품으로 돌아가기",
  };
}

export function ErrorPage({ message }: { message: string }) {
  const exit = errorPageExit(loadSession());
  return (
    <main className="center-page" role="alert">
      <span aria-hidden="true">😅</span>
      <p>{message}</p>
      <div className="center-page-actions">
        <button className="button-secondary" type="button" onClick={() => window.location.reload()}>
          다시 시도
        </button>
        <button className="button-primary" type="button" onClick={() => navigate(exit.path)}>
          {exit.label}
        </button>
      </div>
    </main>
  );
}

export function InlineError({ children }: PropsWithChildren) {
  return <p className="inline-error" role="alert">{children}</p>;
}

/**
 * 글자수 카운터 — `maxLength` 가 걸린 칸 옆에 «지금/최대» 를 보여 준다.
 *
 * 🩸 이게 생긴 이유(2026-08-12) = `maxLength` 는 **아무 말 없이 자른다.** 학생이 가사를
 *    통째로 붙여넣으면 뒷부분이 조용히 사라지는데 화면은 그대로다. 잘린 줄도 모른 채
 *    다음 단계로 간다. 허밍만 「/ 60초」 카운터가 있었고 글자 칸에는 하나도 없었다.
 *
 * 🔑 상한에 닿으면 색이 바뀐다 — 「더 못 쓴다」를 **닿기 전이 아니라 닿는 순간** 알린다.
 *    `aria-live="polite"` 라 화면낭독기도 잘림을 듣는다.
 */
export function CharCounter({ value, max }: { value: string; max: number }) {
  const full = value.length >= max;
  return (
    <div className={full ? "char-counter full" : "char-counter"} aria-live="polite">
      {full ? <span className="sr-only">글자 수가 가득 찼어요. </span> : null}
      {value.length.toLocaleString("ko-KR")}<small>/ {max.toLocaleString("ko-KR")}자</small>
    </div>
  );
}
